import { Box, FormControl, InputLabel, Select, MenuItem, ListSubheader } from "@mui/material";
import { useId } from "react";

interface GroupedOption {
  label: string;
  value: any;
  name?: string;
  id?: any;
  group?: string;
}

interface SelectGroupProps {
  data: GroupedOption[];
  error?: string;
  onChange?: (value: string | null) => void;
  value?: any; // Aceitar qualquer tipo
  placeholder?: string;
  width?: number | string;
  height?: number;
  disabled?: boolean;
}

const SelectGroup = ({ 
    data = [], 
    error, 
    onChange, 
    value, 
    placeholder, 
    width = 300, 
    height = 55, 
    disabled = false 
}: SelectGroupProps) => {
    const uniqueId = useId();    
    
    // Verificar se há dados com agrupamento real
    const itemsWithGroups = data.filter(item => item.group);
    const hasGroups = itemsWithGroups.length > 0;
    
    // Agrupar os dados por categoria apenas se houver grupos
    const groupedData = hasGroups ? data.reduce((groups: { [key: string]: GroupedOption[] }, item) => {
        const group = item.group || 'Sem grupo';
        if (!groups[group]) {
            groups[group] = [];
        }
        groups[group].push(item);
        return groups;
    }, {}) : {};

    // Determinar o valor atual para o select
    const getCurrentValue = () => {
        if (!value) return '';
        
        // Se value é um objeto, extrair o valor
        if (typeof value === 'object' && value !== null) {
            return String(value.id !== undefined ? value.id : value.value || '');
        }
        
        // Se value é string/number, converter para string
        return String(value);
    };

    const currentValue = getCurrentValue();

    const handleChange = (event: any) => {
        const selectedValue = event.target.value;
        onChange?.(selectedValue || null);
    };

    return (
        <Box sx={{ width: width }} key={`select-group-${uniqueId}`}>
            <FormControl 
                fullWidth 
                error={!!error}
                disabled={disabled}
                sx={{ 
                    height: height,
                    backgroundColor: "#fff"
                }}
            >
                <InputLabel id={`select-group-label-${uniqueId}`}>
                    {placeholder}
                </InputLabel>
                <Select
                    labelId={`select-group-label-${uniqueId}`}
                    id={`select-group-${uniqueId}`}
                    value={currentValue}
                    label={placeholder}
                    onChange={handleChange}
                    sx={{ height: height }}
                >
                    <MenuItem value="">
                        <em>Nenhum</em>
                    </MenuItem>
                    
                    {hasGroups ? (
                        // Renderizar com agrupamento
                        Object.entries(groupedData).map(([groupName, groupItems], groupIndex) => [
                            <ListSubheader 
                                key={`group-${groupName}`} 
                                sx={{ 
                                    fontWeight: 'bold',
                                    backgroundColor: 'rgba(77, 176, 177, 0.15)',
                                    color: '#4db0b1',
                                    fontSize: '0.875rem',
                                    lineHeight: '36px',
                                    borderTop: groupIndex > 0 ? '1px solid rgba(77, 176, 177, 0.3)' : 'none',
                                    borderBottom: '1px solid rgba(77, 176, 177, 0.3)',
                                    marginTop: groupIndex > 0 ? '8px' : 0,
                                    paddingTop: '4px',
                                    paddingBottom: '4px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(77, 176, 177, 0.15)',
                                    }
                                }}
                            >
                                {groupName}
                            </ListSubheader>,
                            ...groupItems.map((item) => (
                                <MenuItem 
                                    key={`${groupName}-${item.id || item.value}`}
                                    value={String(item.id !== undefined ? item.id : item.value)}
                                    sx={{
                                        paddingLeft: '24px',
                                        fontSize: '0.875rem',
                                        '&:hover': {
                                            backgroundColor: 'rgba(77, 176, 177, 0.08)',
                                        }
                                    }}
                                >
                                    {item.label || item.name}
                                </MenuItem>
                            ))
                        ]).flat()
                    ) : (
                        // Renderizar sem agrupamento
                        data.map((item) => (
                            <MenuItem 
                                key={item.id || item.value}
                                value={String(item.id !== undefined ? item.id : item.value)}
                            >
                                {item.label || item.name}
                            </MenuItem>
                        ))
                    )}
                </Select>
            </FormControl>
            
            {error && (
                <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {error.toString()}
                </span>
            )}
        </Box>
    );
};

export default SelectGroup;